import { Link } from "react-router-dom";
import { Droplets, Wind, Hammer, AlertTriangle, ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";
import CTABanner from "@/components/sections/CTABanner";
import { canonicalFor } from "@/lib/site";

const PROBLEMS = [
  { icon: Droplets, title: "Active Leaks", desc: "Water staining, dripping ceilings, or attic moisture — we trace it back to the source, no guessing." },
  { icon: Wind, title: "Storm Damage", desc: "Lifted shingles, blown-off flashing, displaced ridge caps after windstorms." },
  { icon: Hammer, title: "Missing or Curled Shingles", desc: "Age, sun damage, or improper installation — replaced and color-matched." },
  { icon: AlertTriangle, title: "Damaged Flashing", desc: "Chimneys, skylights, valleys, vent boots — rebuilt with proper underlayment." },
];

const REPAIR_STEPS = [
  { n: "01", title: "Same-Day Assessment", desc: "For active leaks we dispatch within 24 hours. Photos, written diagnosis, fixed-price quote." },
  { n: "02", title: "Stabilize & Tarp", desc: "If weather demands it, we tarp first to stop interior damage while parts are ordered." },
  { n: "03", title: "Targeted Repair", desc: "Surgical fix — only what needs replacing — with manufacturer-matched materials." },
  { n: "04", title: "Documentation & Warranty", desc: "Before/after photos, written workmanship warranty, insurance-ready paperwork if needed." },
];

export default function RoofRepairPage() {
  return (
    <>
      <Seo
        title="Roof Repair | Crestline Roofing Ltd."
        description="Leaking or damaged roof? Crestline Roofing Ltd. provides fast, professional roof repair across Vancouver and the Fraser Valley. Call us for a same-day assessment."
        canonical={canonicalFor("/services/roof-repair")}
      />
      <PageLayout>
        {({ openEstimate }) => (
          <main data-testid="roof-repair-page" className="bg-[var(--crest-charcoal)] text-white">
            <PageHero
              eyebrow="Roof Repair"
              title="Professional Roof Repair in Vancouver BC"
              subtitle="Same-day assessments. Surgical repairs. Insurance-grade documentation. Serving Vancouver, Burnaby, Surrey, Abbotsford and the entire Fraser Valley."
              image="https://images.unsplash.com/photo-1573876384005-dabc14a8ccbd?crop=entropy&cs=srgb&fm=webp&q=85&w=2400"
              imageAlt="Damaged asphalt shingle roof close-up — Crestline Roofing Ltd. Vancouver"
              breadcrumb={[
                { label: "Home", href: "/" },
                { label: "Services", href: "/services" },
                { label: "Roof Repair" },
              ]}
            />

            {/* Emergency callout */}
            <section className="relative bg-[#0a0a0a] border-y border-white/10 py-6">
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={20} className="text-[var(--crest-amber)]" />
                  <span
                    data-testid="emergency-text"
                    className="font-display uppercase tracking-[0.2em] text-sm font-semibold text-white"
                  >
                    Active leak? We dispatch within 24 hours.
                  </span>
                </div>
                <a
                  href="tel:+17789176817"
                  data-testid="emergency-call"
                  className="group inline-flex items-center gap-2 font-display uppercase tracking-wider font-bold text-sm px-6 py-3 bg-[var(--crest-amber)] text-[#121212] hover:bg-[var(--crest-amber-bright)] transition-colors"
                >
                  <Phone size={16} />
                  Call (778) 917-6817
                </a>
              </div>
            </section>

            {/* Common problems */}
            <section
              data-testid="repair-problems"
              className="relative shingle-texture bg-[var(--crest-slate)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-14">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      What We Fix
                    </span>
                  </div>
                  <h2
                    className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(2rem, 4.8vw, 3.8rem)" }}
                  >
                    Signs You Need a
                    <br />
                    <span className="text-white/50">Roof Repair.</span>
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {PROBLEMS.map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <div
                        key={p.title}
                        data-testid={`problem-${i}`}
                        className="reveal card-lift bg-[#141d2e] border border-white/10 border-t-2 border-t-[var(--crest-amber)] p-7 md:p-8"
                        style={{ transitionDelay: `${i * 70}ms` }}
                      >
                        <Icon size={28} strokeWidth={1.5} className="text-[var(--crest-amber)] mb-4" />
                        <h3 className="font-display uppercase font-bold text-xl text-white tracking-tight mb-3">
                          {p.title}
                        </h3>
                        <p className="font-body text-sm text-white/65 leading-relaxed">{p.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Repair process */}
            <section
              data-testid="repair-process"
              className="relative bg-[var(--crest-charcoal)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl mb-14">
                  <div className="reveal flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-[var(--crest-amber)]" />
                    <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                      Our Repair Process
                    </span>
                  </div>
                  <h2
                    className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                    style={{ fontSize: "clamp(2rem, 4.8vw, 3.8rem)" }}
                  >
                    Our Repair Process.
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {REPAIR_STEPS.map((s, i) => (
                    <div
                      key={s.n}
                      data-testid={`repair-step-${i + 1}`}
                      className="reveal relative"
                      style={{ transitionDelay: `${i * 100}ms` }}
                    >
                      <span className="font-display font-extrabold text-7xl text-white/[0.06] leading-none block mb-4">
                        {s.n}
                      </span>
                      <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)] mb-3 block">
                        Step {s.n}
                      </span>
                      <h3 className="font-display uppercase font-bold text-xl text-white tracking-tight leading-tight mb-3">
                        {s.title}
                      </h3>
                      <p className="font-body text-sm text-white/65 leading-relaxed">{s.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="reveal mt-16 flex justify-center">
                  <Link
                    to="/services/roof-installation"
                    data-testid="link-to-installation"
                    className="group inline-flex items-center gap-2 font-display uppercase tracking-[0.3em] text-xs font-semibold text-white/70 hover:text-[var(--crest-amber)] border-b border-white/20 hover:border-[var(--crest-amber)] pb-1.5 transition-colors"
                  >
                    Need a full replacement instead?
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </section>

            {/* Service area */}
            <section
              data-testid="repair-service-area"
              className="relative bg-[#0d1320] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                  <div className="lg:col-span-5">
                    <div className="reveal flex items-center gap-3 mb-6">
                      <span className="h-px w-12 bg-[var(--crest-amber)]" />
                      <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                        Where We Work
                      </span>
                    </div>
                    <h2
                      className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                      style={{ fontSize: "clamp(1.8rem, 3.8vw, 3rem)" }}
                    >
                      Serving Vancouver, Burnaby, Surrey, Abbotsford &amp; Beyond.
                    </h2>
                    <p className="reveal font-body text-base md:text-lg text-white/65 leading-relaxed mt-6 max-w-md">
                      Repair crews dispatched across the Lower Mainland and Fraser Valley. If you have water coming through the ceiling, call us first — we keep emergency capacity open year-round.
                    </p>
                  </div>
                  <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      "Vancouver",
                      "Burnaby",
                      "Surrey",
                      "Abbotsford",
                      "North Vancouver",
                      "West Vancouver",
                      "Richmond",
                      "Coquitlam",
                      "Port Moody",
                      "New Westminster",
                      "Delta",
                      "Langley",
                      "Maple Ridge",
                      "Pitt Meadows",
                      "Mission",
                    ].map((c, i) => (
                      <div
                        key={c}
                        data-testid={`repair-area-${i}`}
                        className="reveal flex items-center gap-3 p-4 bg-[#161616] border border-white/10"
                        style={{ transitionDelay: `${i * 30}ms` }}
                      >
                        <span className="w-1.5 h-1.5 bg-[var(--crest-amber)]" />
                        <span className="font-display uppercase tracking-[0.15em] text-xs font-semibold text-white/85">
                          {c}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <CTABanner
              onOpenEstimate={openEstimate}
              eyebrow="Active leak?"
              heading="Talk to a Crestline lead today."
              headingTag="h3"
              subheading="Same-day assessments. 48-hour written quote."
              buttonLabel="Request a Repair Quote"
            />
          </main>
        )}
      </PageLayout>
    </>
  );
}
